import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Container, Form, Button, Card, Alert, Spinner } from 'react-bootstrap';
import Select from 'react-select';

import incident_manager from '../api/incident'
import { loadCauses } from '../api/causes';

const OTHER_CAUSE_OPTION = { value: '__other_cause__', label: 'Other' };

const IncidentCommentForm = () => {
    const { token } = useParams()

    const [incident, setIncident] = useState(null);
    const [comment, setComment] = useState('');
    const [causeOptions, setCauseOptions] = useState([]);
    const [selectedCauses, setSelectedCauses] = useState([]);
    const [otherCause, setOtherCause] = useState('');
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(false);

    const raceName = incident?.race_name || '';
    const stageIndex = incident?.stage_race_index ?? incident?.race_stage_index ?? incident?.stage_index ?? incident?.stage?.stage_index;
    const kmToFinish = incident?.km_to_finish ?? incident?.incident_km_from_finish;
    const ridersInvolved = incident?.number_of_riders_involved ?? incident?.incident_number_of_riders;
    const hasOtherCause = selectedCauses.some((cause) => cause.value === OTHER_CAUSE_OPTION.value);

    const stageLabel = (stageIndex) => {
        const numericStageIndex = Number(stageIndex);

        if (!Number.isNaN(numericStageIndex) && numericStageIndex > 0) {
            return `Stage ${numericStageIndex}`;
        }

        return 'One day race';
    };

    const formatDate = (timestamp) => {
        const date = new Date(timestamp);
        return `${date.getDate()}/${date.getMonth() + 1}/${date.getFullYear()}`;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        const causes = selectedCauses.map((cause) => ({
            cause_id: cause.value === OTHER_CAUSE_OPTION.value ? null : cause.value,
            cause_description: cause.label,
            is_other: cause.value === OTHER_CAUSE_OPTION.value
        }));

        const result = await incident_manager.post_rider_incident_comment(token, comment, {
            causes,
            otherCause: hasOtherCause ? otherCause : ''
        });
        if (result) {
            setStatus('success');
            setComment('');
            setSelectedCauses([]);
            setOtherCause('');
        } else {
            setStatus('error');
        }
    };

    useEffect(() => {
        if (!token) window.location.href = '/unauthorized'

        const fetch = async () => {
            const [response, causes] = await Promise.all([
                incident_manager.get_incident_from_token(token),
                loadCauses()
            ]);

            const options = [
                OTHER_CAUSE_OPTION,
                ...causes.map((cause) => ({
                    value: cause.cause_id,
                    label: cause.cause_description
                }))
            ];

            setCauseOptions(options);

            if (response) {
                setIncident(response)
                setFetchError(false);
            } else {
                setFetchError(true);
            }

            setLoading(false);
        }
        fetch();
    }, [token])

    return (
        <Container className="d-flex justify-content-center align-items-center min-vh-100 py-4">
            <Card style={{ width: '100%', maxWidth: '600px' }}>
                <Card.Body>
                    <Card.Title className="text-center mb-4">Incident Comment</Card.Title>

                    {loading && (
                        <div className="d-flex justify-content-center py-5">
                            <Spinner animation="border" role="status" />
                        </div>
                    )}


                    {fetchError && !loading && (
                        <Alert variant="danger">
                            ❌ Unable to load incident data. The token may be invalid, previously used or expired.
                        </Alert>
                    )}

                    {!loading && incident && (
                        <>
                            {status === 'success' && (
                                <Alert variant="success" onClose={() => setStatus(null)} dismissible>
                                    ✅ Comment submitted successfully.
                                </Alert>
                            )}

                            {status === 'error' && (
                                <Alert variant="danger" onClose={() => setStatus(null)} dismissible>
                                ❌ Failed to submit comment. Please try again.
                                </Alert>
                            )}

                            <Form onSubmit={handleSubmit}>
                                <Form.Group controlId="incidentDate" className="mb-3">
                                    <Form.Label>Date</Form.Label>
                                    <Form.Control type="text" readOnly value={formatDate(incident?.incident_timestamp)} />
                                </Form.Group>
                                <Form.Group controlId="raceName" className="mb-3">
                                    <Form.Label>Race Name</Form.Label>
                                    <Form.Control type="text" readOnly value={raceName} />
                                </Form.Group>
                                <Form.Group controlId="stageNumber" className="mb-3">
                                    <Form.Label>Stage Number</Form.Label>
                                    <Form.Control type="text" readOnly value={stageLabel(stageIndex)} />
                                </Form.Group>
                                <Form.Group controlId="incidentDistance" className="mb-3">
                                    <Form.Label>km to Finish</Form.Label>
                                    <Form.Control type="text" readOnly value={kmToFinish ?? ''} />
                                </Form.Group>
                                <Form.Group controlId="ridersInvolved" className="mb-3">
                                    <Form.Label>Number of Riders Involved</Form.Label>
                                    <Form.Control type="text" readOnly value={ridersInvolved ?? ''} />
                                </Form.Group>
                                <Form.Group controlId="incidentCauses" className="mb-3">
                                    <Form.Label>Incident Causes</Form.Label>
                                    <Select
                                        isMulti
                                        name="incident-causes"
                                        options={causeOptions}
                                        value={selectedCauses}
                                        onChange={(options) => setSelectedCauses(options || [])}
                                    />
                                </Form.Group>
                                {hasOtherCause && (
                                    <Form.Group controlId="otherCause" className="mb-3">
                                        <Form.Label>Other Cause</Form.Label>
                                        <Form.Control
                                            type="text"
                                            placeholder="Describe the other cause..."
                                            value={otherCause}
                                            onChange={(e) => setOtherCause(e.target.value)}
                                            required={hasOtherCause}
                                        />
                                    </Form.Group>
                                )}
                                <Form.Group controlId="userComment" className="mb-3">
                                    <Form.Label>Your Comment</Form.Label>
                                    <Form.Text className="text-muted fst-italic d-block mb-2">
                                        Please describe what happened during the crash, any causes you identified or observed, and any improvements you believe could help prevent similar incidents.
                                    </Form.Text>
                                    <Form.Control
                                        as="textarea"
                                        rows={4}
                                        placeholder="Write your comment here..."
                                        value={comment}
                                        onChange={(e) => setComment(e.target.value)}
                                    />
                                </Form.Group>
                                <p className="text-muted small mb-3">
                                    Disclaimer: Your response will be used to better understand incident circumstances and improve rider safety processes.
                                </p>
                                <Button variant="primary" type="submit" className="w-100">Submit Comment</Button>
                            </Form>
                        </>
                    )}
                </Card.Body>
            </Card>
        </Container>
    );
}

export default IncidentCommentForm;
